import { useMemo } from 'react';
import { useQuery } from 'react-query';
import policies from 'shared/configs/rbac/accessPolicies';
import { EntityType } from 'shared/constants/entityTypes';
import { DEFAULT_PAGE_SIZE } from 'shared/constants/misc';
import { axiosinstance } from 'shared/utils/api';
import useAccessControl from '../misc/useAccessControl';

export function fetchPortalLogo() {
	return axiosinstance
		.get(`/v1/logo`, { responseType: 'blob' })
		.then(res => res.data);
}

export const useBillingMetrics = () => {
	const fetchData = () =>
		axiosinstance.get(`/v1/billing`).then(res => res.data);
	const { data, ...queryResult } = useQuery(
		['metrics', 'billing'],
		fetchData
	);

	return {
		data: data?.data || {},
		...queryResult,
	};
};

export function useRecentReports({ daysDelta }) {
  const baseKey = 'recentReports';
  const baseUrl = '/v1/reports/recent';
  const url =
    typeof daysDelta === 'boolean'
      ? baseUrl
      : `${baseUrl}/${parseInt(daysDelta)}`;

  function mapAttachmentsToReports(data = {}) {
    const { reports = [], attachments = {} } = data;
    return reports.map(({ order, sample, test, ...report }) => ({
      entity: order || sample || test || {},
      entityType: !!order
        ? EntityType.ORDER
        : !!sample
        ? EntityType.SAMPLE
        : EntityType.TEST,
      attachments: attachments[report.id],
      report,
    }));
  }

  const canViewReports = useAccessControl(policies.route.reports);
  const { data = [], ...query } = useQuery(
    [baseKey],
    async () => {
      const res = await axiosinstance.get(url);
      return mapAttachmentsToReports(res.data?.data);
    },
    { enabled: canViewReports }
  );

  return { data, ...query };
}

export const useReportPDFbyId = (type, id) => {
	const fetchData = () =>
		id
			? axiosinstance
					.get(`/v1/${type}/${id}/report/pdf`, {
						responseType: 'arraybuffer',
					})
					.then(res => res.data)
			: null;

	const canViewReports = useAccessControl(policies.ui.reportDownloadBtn);
	const { data, isLoading, error, refetch, isFetching } = useQuery(
		['reportPdf', type, parseInt(id)],
		fetchData,
		{
			refetchOnWindowFocus: false,
			enabled: false,
		}
	);

	return [
    data,
    {
      error,
      isLoading,
      isFetching,
      refetch: () => canViewReports && refetch(),
    },
  ];
};

export function useRequestParams({
  page = 1,
  search = '',
  pageSize: page_size = DEFAULT_PAGE_SIZE,
  ...rest
}) {
  const params = useMemo(
    () => ({
      page,
			page_size,
      ...rest,
      ...(Boolean(search) && { search }),
    }),
    [page, search, page_size, rest]
  );
  return params;
}

export function usePortalLogo() {
	const { data } = useQuery(
		['logo'],
		fetchPortalLogo
	);
	return data;
}